'use client';

import React, { useState } from 'react';
import { Mail, UserPlus, Search, X, Check, Copy, RefreshCw, Ban, Eye, AlertTriangle, Shield, Building2, Users, Briefcase, Send, CheckCircle2, Info,  } from 'lucide-react';
import {
  MOCK_INVITATIONS, FARMS_LIST, DEPARTMENTS, INVITATION_STATUS_STYLES,
  ROLE_DESCRIPTIONS, formatDate, isExpiringSoon,
  StaffInvitation, InvitationStatus,
} from './staffInvitationsData';
import { UserRole, ROLE_LABELS, ROLE_COLORS } from '@/lib/rbac';
import { useRole } from '@/lib/useRole';

// ─── Role Selector Card ───────────────────────────────────────────────────────
function RoleCard({ role, selected, onSelect }: { role: UserRole; selected: boolean; onSelect: () => void }) {
  const colors = ROLE_COLORS[role];
  const icons: Record<UserRole, React.ReactNode> = {
    farm_owner: <Shield size={18} />,
    manager: <Briefcase size={18} />,
    technician: <Users size={18} />,
    field_staff: <Building2 size={18} />,
  };
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-xl border-2 transition-all duration-150 ${
        selected
          ? 'border-teal-500 bg-teal-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 p-1.5 rounded-lg ${colors.bg} ${colors.text}`}>
          {icons[role]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{ROLE_LABELS[role]}</span>
            {selected && <Check size={14} className="text-teal-600 flex-shrink-0" />}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{ROLE_DESCRIPTIONS[role]}</p>
        </div>
      </div>
    </button>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: InvitationStatus }) {
  const s = INVITATION_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// ─── Invite Detail Drawer ─────────────────────────────────────────────────────
function InviteDrawer({
  invite,
  onClose,
  onActivate,
  onCancel,
  onResend,
}: {
  invite: StaffInvitation;
  onClose: () => void;
  onActivate: (id: string) => void;
  onCancel: (id: string) => void;
  onResend: (id: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const colors = ROLE_COLORS[invite.role];
  const expiring = isExpiringSoon(invite.expiresAt);

  function handleCopy() {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-bold text-gray-900">Invitation Details</h3>
            <p className="text-xs text-gray-400 mt-0.5">{invite.id}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {/* Staff Info */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
              {invite.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <p className="text-base font-bold text-gray-900">{invite.name}</p>
              <p className="text-sm text-gray-500">{invite.email}</p>
              <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                {ROLE_LABELS[invite.role]}
              </span>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
            <span className="text-sm text-gray-600 font-medium">Status</span>
            <StatusBadge status={invite.status} />
          </div>

          {/* Expiry warning */}
          {expiring && invite.status === 'pending' && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <AlertTriangle size={15} className="text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 font-medium">Expires in less than 3 days</p>
            </div>
          )}

          {/* Details Grid */}
          <div className="space-y-3">
            {[
              { label: 'Farm / Branch', value: invite.farm },
              { label: 'Department', value: invite.department },
              { label: 'Invited By', value: invite.invitedBy },
              { label: 'Invited On', value: formatDate(invite.invitedAt) },
              { label: 'Expires On', value: formatDate(invite.expiresAt) },
              ...(invite.activatedAt ? [{ label: 'Activated On', value: formatDate(invite.activatedAt) }] : []),
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-xs text-gray-400 font-medium">{label}</span>
                <span className="text-sm text-gray-800 font-medium">{value}</span>
              </div>
            ))}
          </div>

          {/* Activation Code */}
          {invite.status === 'pending' && (
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
              <p className="text-xs text-blue-600 font-semibold mb-2 uppercase tracking-wide">Activation Code</p>
              <div className="flex items-center justify-between gap-3">
                <code className="text-lg font-bold text-blue-800 tracking-widest">{invite.activationCode}</code>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-semibold transition-colors"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-blue-500 mt-2">Share this code with the staff member to activate their account on first login.</p>
            </div>
          )}

          {/* Notes */}
          {invite.notes && (
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
              <p className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-wide">Notes</p>
              <p className="text-sm text-gray-700">{invite.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {invite.status !== 'activated' && invite.status !== 'cancelled' && (
          <div className="px-5 py-4 border-t border-gray-100 space-y-2 sticky bottom-0 bg-white">
            {invite.status === 'pending' && (
              <button
                onClick={() => onActivate(invite.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors"
              >
                <CheckCircle2 size={16} />
                Activate Account
              </button>
            )}
            {(invite.status === 'pending' || invite.status === 'expired') && (
              <button
                onClick={() => onResend(invite.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold transition-colors"
              >
                <RefreshCw size={15} />
                Resend Invitation
              </button>
            )}
            {invite.status === 'pending' && (
              <button
                onClick={() => onCancel(invite.id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold transition-colors"
              >
                <Ban size={15} />
                Cancel Invitation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function StaffInvitationsContent() {
  const { can, isManagerOrAbove } = useRole();
  const canManage = isManagerOrAbove;

  const [invitations, setInvitations] = useState<StaffInvitation[]>(MOCK_INVITATIONS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvitationStatus | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<StaffInvitation | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Form state
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: '' as UserRole | '',
    farm: '',
    department: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // ─── Filters ────────────────────────────────────────────────────────────────
  const filtered = invitations.filter((inv) => {
    const matchSearch =
      inv.name.toLowerCase().includes(search.toLowerCase()) ||
      inv.email.toLowerCase().includes(search.toLowerCase()) ||
      inv.farm.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    const matchRole = roleFilter === 'all' || inv.role === roleFilter;
    return matchSearch && matchStatus && matchRole;
  });

  // ─── Summary counts ──────────────────────────────────────────────────────────
  const counts = {
    total: invitations.length,
    pending: invitations.filter(i => i.status === 'pending').length,
    activated: invitations.filter(i => i.status === 'activated').length,
    expired: invitations.filter(i => i.status === 'expired').length,
  };

  // ─── Form validation ─────────────────────────────────────────────────────────
  function validate() {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Full name is required';
    if (!form.email.trim()) errors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email address';
    if (!form.role) errors.role = 'Please select a role';
    if (!form.farm) errors.farm = 'Please select a farm or branch';
    if (!form.department) errors.department = 'Please select a department';
    return errors;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setSubmitting(true);
    setTimeout(() => {
      const newInvite: StaffInvitation = {
        id: `inv-${Date.now()}`,
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role as UserRole,
        farm: FARMS_LIST.find(f => f.id === form.farm)?.label ?? form.farm,
        department: form.department,
        invitedBy: 'Okello James',
        invitedAt: new Date().toISOString().split('T')[0],
        expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'pending',
        activationCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
        notes: form.notes.trim() || undefined,
      };
      setInvitations(prev => [newInvite, ...prev]);
      setForm({ name: '', email: '', role: '', farm: '', department: '', notes: '' });
      setFormErrors({});
      setSubmitting(false);
      setShowForm(false);
      setSuccessMsg(`Invitation sent to ${newInvite.email}`);
      setTimeout(() => setSuccessMsg(''), 4000);
    }, 800);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────
  function handleActivate(id: string) {
    setInvitations(prev =>
      prev.map(inv =>
        inv.id === id
          ? { ...inv, status: 'activated', activatedAt: new Date().toISOString().split('T')[0] }
          : inv
      )
    );
    setSelectedInvite(null);
    setSuccessMsg('Account activated successfully');
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  function handleCancel(id: string) {
    setInvitations(prev => prev.map(inv => inv.id === id ? { ...inv, status: 'cancelled' } : inv));
    setSelectedInvite(null);
  }

  function handleResend(id: string) {
    setInvitations(prev =>
      prev.map(inv =>
        inv.id === id
          ? {
              ...inv,
              status: 'pending',
              invitedAt: new Date().toISOString().split('T')[0],
              expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              activationCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
            }
          : inv
      )
    );
    setSelectedInvite(null);
    setSuccessMsg('Invitation resent successfully');
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  // ─── Access Guard ─────────────────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Shield size={28} className="text-red-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-sm text-gray-500">Only farm owners and managers can invite and manage staff accounts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Success Toast */}
      {successMsg && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl bg-teal-600 text-white text-sm font-semibold shadow-lg">
          <CheckCircle2 size={16} />
          {successMsg}
        </div>
      )}

      {/* Page Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Staff Invitations</h1>
            <p className="text-sm text-gray-500 mt-0.5">Invite team members, assign roles, and activate accounts before first login</p>
          </div>
          {canManage && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors shadow-sm"
            >
              <UserPlus size={16} />
              Invite Staff
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Invitations', value: counts.total, color: 'text-gray-900', bg: 'bg-gray-50', border: 'border-gray-200' },
          { label: 'Pending', value: counts.pending, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
          { label: 'Activated', value: counts.activated, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
          { label: 'Expired', value: counts.expired, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
        ].map(({ label, value, color, bg, border }) => (
          <div key={label} className={`p-4 rounded-xl border ${bg} ${border}`}>
            <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="px-6 pb-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, farm…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as InvitationStatus | 'all')}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="activated">Activated</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-700"
        >
          <option value="all">All Roles</option>
          <option value="farm_owner">Farm Owner</option>
          <option value="manager">Manager</option>
          <option value="technician">Technician</option>
          <option value="field_staff">Field Staff</option>
        </select>
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="flex-1 px-6 pb-6 overflow-auto">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Staff Member</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Farm</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Invited</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                    No invitations found matching your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((inv) => {
                  const colors = ROLE_COLORS[inv.role];
                  const expiring = isExpiringSoon(inv.expiresAt) && inv.status === 'pending';
                  return (
                    <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {inv.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{inv.name}</p>
                            <p className="text-xs text-gray-400">{inv.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}>
                          {ROLE_LABELS[inv.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm hidden md:table-cell">{inv.farm}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell">{formatDate(inv.invitedAt)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`text-xs ${expiring ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}>
                          {expiring && <AlertTriangle size={11} className="inline mr-1" />}
                          {formatDate(inv.expiresAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={inv.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setSelectedInvite(inv)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                            title="View details"
                          >
                            <Eye size={15} />
                          </button>
                          {inv.status === 'pending' && (
                            <button
                              onClick={() => handleActivate(inv.id)}
                              className="p-1.5 rounded-lg hover:bg-teal-50 text-gray-400 hover:text-teal-600 transition-colors"
                              title="Activate account"
                            >
                              <CheckCircle2 size={15} />
                            </button>
                          )}
                          {(inv.status === 'pending' || inv.status === 'expired') && (
                            <button
                              onClick={() => handleResend(inv.id)}
                              className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Resend invitation"
                            >
                              <RefreshCw size={14} />
                            </button>
                          )}
                          {inv.status === 'pending' && (
                            <button
                              onClick={() => handleCancel(inv.id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                              title="Cancel invitation"
                            >
                              <Ban size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <div
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
                  <UserPlus size={18} className="text-teal-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Invite Staff Member</h2>
                  <p className="text-xs text-gray-400">Invitation valid for 14 days</p>
                </div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Mail size={14} className="text-teal-600" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="e.g. Amara Nakato"
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 ${formErrors.name ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      />
                      {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="e.g. amara@example.com"
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 ${formErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      />
                      {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
                    </div>
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Shield size={14} className="text-teal-600" />
                    Assign Role <span className="text-red-500">*</span>
                  </h3>
                  {formErrors.role && <p className="text-xs text-red-500 mb-2">{formErrors.role}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(['farm_owner', 'manager', 'technician', 'field_staff'] as UserRole[]).map((role) => (
                      <RoleCard
                        key={role}
                        role={role}
                        selected={form.role === role}
                        onSelect={() => setForm(f => ({ ...f, role }))}
                      />
                    ))}
                  </div>
                </div>

                {/* Assignment */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Building2 size={14} className="text-teal-600" />
                    Farm Assignment
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Farm / Branch <span className="text-red-500">*</span></label>
                      <select
                        value={form.farm}
                        onChange={(e) => setForm(f => ({ ...f, farm: e.target.value }))}
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white ${formErrors.farm ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      >
                        <option value="">Select farm…</option>
                        {FARMS_LIST.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                      </select>
                      {formErrors.farm && <p className="text-xs text-red-500 mt-1">{formErrors.farm}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Department <span className="text-red-500">*</span></label>
                      <select
                        value={form.department}
                        onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))}
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white ${formErrors.department ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                      >
                        <option value="">Select department…</option>
                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      {formErrors.department && <p className="text-xs text-red-500 mt-1">{formErrors.department}</p>}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes (optional)</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Any additional context for this invitation…"
                    rows={2}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                </div>

                {/* Info Banner */}
                <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
                  <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 leading-relaxed">
                    An activation code will be generated and shown after sending. Share it with the staff member — they will need it to set up their account on first login. The invitation expires in <strong>14 days</strong>.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                >
                  {submitting ? (
                    <><RefreshCw size={15} className="animate-spin" /> Sending…</>
                  ) : (
                    <><Send size={15} /> Send Invitation</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {selectedInvite && (
        <InviteDrawer
          invite={selectedInvite}
          onClose={() => setSelectedInvite(null)}
          onActivate={handleActivate}
          onCancel={handleCancel}
          onResend={handleResend}
        />
      )}
    </div>
  );
}
